/**
 * /producao/lotes/:id — Detalhe do Lote (Sprint P2).
 *
 * Zona 1 — EntityHeader com voltar para Otimização + breadcrumb
 *          `Produção / Otimização / LT-UN1-N` + badges situação/origem.
 * Zona 2 — KpiStrip (situação, ambientes, peças, requisição, sobra %).
 * Zona 3 — Ações contextuais por situação + ambientes do lote (cards
 *          compact clicáveis) + plano de corte read-only.
 *
 * Máquina de estados do lote: ABERTO -> OTIMIZADO -> LIBERADO (sem
 * cancelado — peças/ambientes saem individualmente). Ações:
 *   ABERTO    -> "Registrar plano externo" (slide-over) | "Otimizar (mock)"
 *   OTIMIZADO -> "Gerar requisição de compra" | "Liberar lote" (slide-over
 *                de confirmação — materializa as peças)
 *   LIBERADO  -> sem ações (estado final; badges contam a história).
 * Toda action retorna o Detalhe atualizado (a tela substitui o estado).
 *
 * Lote C (§2.4 — card-lote ATÔMICO): bloco "Etapa do lote" quando o
 * backend expõe `etapa_codigo` — etapa corrente do LOTE + Iniciar/Concluir
 * EM BLOCO (cascateia aos membros; resposta {concluidos, pulados} listada
 * inline). Gate por fase (producao_fase:operar_f{n}); etapa-checkpoint
 * (USINAGEM) não tem botões manuais — pill "conclui por bipagem" (§2.5).
 * Backend sem Lote C (sem etapa_codigo) => bloco não renderiza.
 */
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import AmbienteCard from "../../components/producao/AmbienteCard";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  imprimirEtiquetasOpLote,
  listarOrdensProducao,
} from "../../services/producao";
import {
  ETAPA_INFO_POR_CODIGO,
  ETAPA_LABELS,
  FASE_INFO,
  SITUACAO_LOTE_LABELS,
  SITUACAO_LOTE_TONES,
  boardPorEtapa,
  concluirEtapaLote,
  erroEhCheckpointBipagem,
  gerarRequisicao,
  iniciarEtapaLote,
  liberarLote,
  obterLote,
  otimizarMock,
  permissaoOperarFase,
  registrarPlano,
} from "../../services/producaoV2";
import { formatarDataHoraCurta } from "../../utils/dataHora";
import { PageContainer } from "../../styles/shared";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  InfoMsg,
  Pill,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  WarningMsg,
} from "../Orcamentos/styles";
import { useCheckpointsBipagem } from "./_shared/useCheckpointsBipagem";
import { PERMITE_INTEGRACAO_FICTICIA } from "../../config/ambiente";

const GradeAmbientes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
`;

const LinhaAcoes = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const DicaAcao = styled.p`
  font-size: 12px;
  color: var(--ink-3);
  margin: 0;
`;

const TabelaChapas = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-3);
    padding: 8px 10px;
    border-bottom: 1px solid var(--border);
  }

  td {
    padding: 8px 10px;
    border-top: 1px solid var(--border-subtle);
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

const ResumoPlano = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  font-size: 13px;
  color: var(--ink-2);

  strong {
    color: var(--ink);
  }
`;

const CampoForm = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const RotuloCampo = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
`;

const DicaCampo = styled.p`
  font-size: 12px;
  color: var(--ink-3);
  margin: 4px 0 0;
`;

const CampoInput = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.88rem;
  font-family: inherit;

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const AreaTexto = styled.textarea`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.88rem;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const RodapePainel = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const LinhaErro = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

/** Linha de pills da etapa corrente do lote (Lote C). */
const LinhaEtapaLote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ListaPulados = styled.ul`
  margin: 6px 0 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12.5px;
`;

// ---------------------------------------------------------------------------
// Slide-overs
// ---------------------------------------------------------------------------

function RegistrarPlanoForm({ aberto, onFechar, loteId, onAtualizado }) {
  const { request } = useAuth();
  const [sobra, setSobra] = useState("");
  const [observacao, setObservacao] = useState("");

  useEffect(() => {
    if (aberto) {
      setSobra("");
      setObservacao("");
    }
  }, [aberto]);

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Registrar plano externo"
      subtitle="O lote foi otimizado fora do ERP (3D / otimizador próprio) — aqui fica o registro oficial"
      footer={
        <RodapePainel>
          <SecondaryButton type="button" onClick={onFechar}>
            Voltar
          </SecondaryButton>
          <AsyncButton
            silentError
            successMessage="Plano registrado — lote otimizado."
            onClick={async () => {
              const obs = observacao.trim();
              const resp = await registrarPlano(request, loteId, {
                // O endpoint aceita um JSON livre de plano — o registro
                // externo persiste a observação dentro dele (o ERP é o
                // sistema de REGISTRO; detalhes do corte ficam no otimizador).
                plano: obs ? { registro: "externo", observacao: obs } : undefined,
                sobraPercentual: sobra !== "" ? sobra : undefined,
              });
              onAtualizado(resp);
              onFechar();
            }}
          >
            Registrar plano
          </AsyncButton>
        </RodapePainel>
      }
    >
      <CampoForm>
        <RotuloCampo>Sobra estimada (%)</RotuloCampo>
        <CampoInput
          type="number"
          min={0}
          max={100}
          step="0.1"
          value={sobra}
          onChange={(e) => setSobra(e.target.value)}
          placeholder="Ex.: 12.5 (opcional)"
        />
        <DicaCampo>
          Percentual de sobra/refilo apontado pelo otimizador externo —
          alimenta o KPI de aproveitamento do lote.
        </DicaCampo>
      </CampoForm>
      <CampoForm>
        <RotuloCampo>Observação</RotuloCampo>
        <AreaTexto
          rows={3}
          maxLength={300}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Onde foi otimizado, referência do arquivo, etc (opcional)"
        />
      </CampoForm>
    </SlideOverPanel>
  );
}

function LiberarForm({ aberto, onFechar, lote, onAtualizado }) {
  const { request } = useAuth();

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Liberar lote"
      subtitle={lote ? `${lote.numero} · ${lote.ambientes_count} ambiente(s)` : undefined}
      footer={
        <RodapePainel>
          <SecondaryButton type="button" onClick={onFechar}>
            Voltar
          </SecondaryButton>
          <AsyncButton
            silentError
            successMessage="Lote liberado — peças materializadas."
            onClick={async () => {
              const resp = await liberarLote(request, lote.id);
              onAtualizado(resp);
              onFechar();
            }}
          >
            Liberar lote
          </AsyncButton>
        </RodapePainel>
      }
    >
      <WarningMsg style={{ marginBottom: 16 }}>
        Liberar o lote CRIA as peças de produção dos ambientes (a partir do
        BOM de cada um) e move o fluxo para a industrialização. A liberação
        não pode ser desfeita — peças indevidas precisam ser canceladas
        individualmente.
      </WarningMsg>
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export default function DetalheLote() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  const toast = useToast();

  const [lote, setLote] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [painelAcao, setPainelAcao] = useState(null); // registrar-plano|liberar
  // Lote C — resultado da última ação em bloco ({acao, quantidade, pulados})
  // e erro inline das ações de etapa (request roda com silentError).
  const [resultadoBloco, setResultadoBloco] = useState(null);
  const [erroEtapaLote, setErroEtapaLote] = useState(null);
  // {etapaCodigo, dessincronizado} derivados do BOARD quando o serializer
  // do Detalhe não expõe `etapa_codigo` (a etapa corrente do lote é
  // calculada no board — etapa mais atrás entre os membros).
  const [etapaBoard, setEtapaBoard] = useState(null);
  const checkpoints = useCheckpointsBipagem();

  const podeOtimizar = hasPermission("lote:otimizar");
  const podeLiberar = hasPermission("lote:liberar");
  const podeImprimir = hasPermission("etiqueta:imprimir");

  // Etiquetas Code 128 do lote inteiro (bulk F8.3) — 1 página por peça.
  // Se qualquer peça já foi impressa, o backend responde 400
  // {reimpressao:true} e pedimos o motivo antes de refazer.
  const imprimirEtiquetasDoLote = useCallback(async () => {
    let pecas;
    try {
      const resposta = await listarOrdensProducao(request, { lote: id });
      pecas = Array.isArray(resposta) ? resposta : resposta?.results || [];
    } catch (err) {
      toast.error(err?.message || "Não foi possível listar as peças do lote.");
      return;
    }
    const opIds = pecas
      .filter((p) => p.situacao !== "CANCELADA")
      .map((p) => p.id);
    if (opIds.length === 0) {
      toast.error("Nenhuma peça ativa no lote para etiquetar.");
      return;
    }
    try {
      await imprimirEtiquetasOpLote(request, { opIds });
      toast.success(`Etiquetas geradas (${opIds.length} peças).`);
    } catch (err) {
      if (!err?.reimpressao) {
        toast.error(err?.message || "Falha ao gerar etiquetas.");
        return;
      }
      const motivo = window.prompt(
        "Alguma peça do lote já teve etiqueta impressa. Informe o motivo da reimpressão:",
        "",
      );
      if (!motivo || !motivo.trim()) {
        toast.error("Reimpressão cancelada (motivo vazio).");
        return;
      }
      try {
        await imprimirEtiquetasOpLote(request, {
          opIds,
          motivo: motivo.trim(),
        });
        toast.success(`Etiquetas reimpressas (${opIds.length} peças).`);
      } catch (err2) {
        toast.error(err2?.message || "Falha ao reimprimir.");
      }
    }
  }, [request, id, toast]);

  // `silencioso` recarrega sem desmontar a tela (pós-ação em bloco — o
  // resultado inline não pode sumir atrás do "Carregando…").
  const carregar = useCallback(async ({ silencioso } = {}) => {
    setErro(null);
    if (!silencioso) setCarregando(true);
    try {
      setLote(await obterLote(request, id));
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar o lote.");
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, [request, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Fallback da etapa corrente: enquanto o Detalhe não expõe
  // `etapa_codigo`, localiza a entrada deste lote no board (mesma fonte
  // da verdade e mesmo gate de permissão). Depende do OBJETO `lote`
  // (nova referência a cada carregar) de propósito — pós-ação em bloco a
  // etapa do lote avançou e o board precisa ser relido. Quando o backend
  // passar a expor o campo no Detalhe, este fetch deixa de rodar.
  useEffect(() => {
    if (!lote?.id || lote.etapa_codigo) {
      setEtapaBoard(null);
      return undefined;
    }
    let ativo = true;
    boardPorEtapa(request)
      .then((board) => {
        if (!ativo) return;
        let achado = null;
        for (const etapa of board?.etapas || []) {
          for (const entrada of etapa.ambientes || []) {
            if (entrada?.tipo === "lote" && entrada.lote?.id === lote.id) {
              achado = {
                etapaCodigo: entrada.etapa_codigo || etapa.codigo,
                dessincronizado: Boolean(entrada.dessincronizado),
              };
            }
          }
        }
        setEtapaBoard(achado);
      })
      .catch(() => {
        /* sem board (permissão/erro), o bloco simplesmente não renderiza */
      });
    return () => {
      ativo = false;
    };
  }, [request, lote]);

  if (carregando) {
    return (
      <PageContainer>
        <EntityHeader
          onBack={() => navigate("/producao/otimizacao")}
          backLabel="Otimização"
          title="Carregando lote…"
        />
      </PageContainer>
    );
  }

  if (erro || !lote) {
    return (
      <PageContainer>
        <EntityHeader
          onBack={() => navigate("/producao/otimizacao")}
          backLabel="Otimização"
          title="Detalhe do Lote"
        />
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>
            {erro || "Lote não encontrado."}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      </PageContainer>
    );
  }

  const situacaoLabel =
    lote.situacao_display || SITUACAO_LOTE_LABELS[lote.situacao] || lote.situacao;

  // ---- Lote C: etapa corrente do LOTE ----
  // Preferência: campo do Detalhe (quando o backend expuser) -> derivado
  // do board (fallback) -> ausente = bloco não renderiza (backend sem
  // Lote C ou lote fora da janela PLANO_CORTE..USINAGEM).
  const etapaCodigoLote = lote.etapa_codigo || etapaBoard?.etapaCodigo || null;
  const etapaInfoLote = etapaCodigoLote
    ? ETAPA_INFO_POR_CODIGO[etapaCodigoLote]
    : null;
  const etapaLoteLabel = etapaCodigoLote
    ? lote.etapa_label
      || etapaInfoLote?.label
      || ETAPA_LABELS[etapaCodigoLote]
      || etapaCodigoLote
    : null;
  const faseEtapaLote = lote.etapa_fase || etapaInfoLote?.fase || null;
  const dessincronizadoLote = Boolean(
    lote.dessincronizado ?? etapaBoard?.dessincronizado,
  );
  const ehCheckpointEtapaLote =
    Boolean(etapaCodigoLote) && checkpoints.has(etapaCodigoLote);
  // Mesmo gate por FASE do board/Detalhe do Ambiente (D1.1) — o gate real
  // é o 403 do backend; aqui é UX (esconder botão de quem não opera).
  const permissaoFaseLote = faseEtapaLote
    ? permissaoOperarFase(faseEtapaLote)
    : null;
  const podeOperarEtapaLote =
    Boolean(permissaoFaseLote) && hasPermission(permissaoFaseLote);

  const executarEtapaLote = async (acao) => {
    setErroEtapaLote(null);
    setResultadoBloco(null);
    const fn = acao === "iniciar" ? iniciarEtapaLote : concluirEtapaLote;
    try {
      const resp = await fn(request, lote.id, etapaCodigoLote, {
        silentError: true,
      });
      const pulados = Array.isArray(resp?.pulados) ? resp.pulados : [];
      const quantidade = resp?.concluidos ?? resp?.iniciados ?? 0;
      setResultadoBloco({ acao, quantidade, pulados });
      toast.success(
        `Etapa ${etapaLoteLabel} ${
          acao === "iniciar" ? "iniciada" : "concluída"
        } em bloco — ${quantidade} ambiente(s)${
          pulados.length > 0 ? `, ${pulados.length} pulado(s)` : ""
        }.`,
      );
      await carregar({ silencioso: true });
    } catch (err) {
      setErroEtapaLote(
        erroEhCheckpointBipagem(err)
          ? `«${etapaLoteLabel}» é um checkpoint de bipagem — a conclusão `
            + "acontece peça a peça no Terminal, não por aqui."
          : err?.payload?.detalhe
            || err?.message
            || `Não foi possível ${acao} a etapa do lote.`,
      );
      throw err;
    }
  };

  const plano = lote.plano_corte || null;
  const chapasPlano = Array.isArray(plano?.chapas) ? plano.chapas : [];
  const temPlanoEstruturado = chapasPlano.length > 0;

  const itensKpi = [
    { label: "Situação", value: situacaoLabel },
    { label: "Ambientes", value: lote.ambientes_count ?? 0 },
    {
      label: "Peças",
      value: lote.pecas_count ?? 0,
      sub: lote.situacao === "LIBERADO"
        ? "materializadas na liberação"
        : "materializam ao liberar",
    },
    {
      label: "Requisição",
      value: lote.requisicao_numero || "—",
      sub: lote.requisicao_numero
        ? "acompanhe em Suprimentos"
        : "gere após otimizar",
    },
    {
      label: "Sobra",
      value:
        lote.sobra_percentual != null ? `${lote.sobra_percentual}%` : "–",
      sub: "aproveitamento do plano",
    },
  ];

  return (
    <PageContainer>
      <EntityHeader
        onBack={() => navigate("/producao/otimizacao")}
        backLabel="Otimização"
        title={lote.numero}
        subtitle={`Produção / Otimização / ${lote.numero}`}
        badges={[
          {
            key: "situacao",
            label: situacaoLabel,
            tone: SITUACAO_LOTE_TONES[lote.situacao] || "neutral",
          },
          {
            key: "origem",
            label: lote.origem_display || lote.origem,
            tone: "neutral",
          },
          // Plano de Suprimentos §7: o lote foi enviado ao PharuCut.
          ...(lote.cut_batch_id
            ? [{ key: "cut", label: `PharuCut ${lote.cut_batch_id}`, tone: "info" }]
            : []),
        ]}
      />

      <KpiStrip items={itensKpi} />

      {/* Lote C — etapa corrente do LOTE + ações em bloco. O bloco fica
          visível após uma ação mesmo se o lote sair da janela de etapas
          (etapa_codigo null) para o resultado não sumir. */}
      {(etapaCodigoLote || resultadoBloco) && (
        <Card>
          <SectionTitle>Etapa do lote</SectionTitle>
          <LinhaEtapaLote>
            {etapaCodigoLote ? (
              <>
                <Pill $tone="info">{etapaLoteLabel}</Pill>
                {faseEtapaLote && (
                  <Pill $tone="neutral">
                    {FASE_INFO[faseEtapaLote]?.rotulo || faseEtapaLote}
                  </Pill>
                )}
                {ehCheckpointEtapaLote && (
                  <Pill
                    $tone="neutral"
                    title="Etapa-checkpoint: inicia e conclui peça a peça pela bipagem no Terminal — sem conclusão manual"
                  >
                    conclui por bipagem
                  </Pill>
                )}
                {dessincronizadoLote && (
                  <Pill
                    $tone="warning"
                    title="A etapa de algum ambiente membro diverge da etapa do lote (avanço registrado fora do lote) — concluir a etapa do lote realinha os membros aplicáveis"
                  >
                    dessincronizado
                  </Pill>
                )}
              </>
            ) : (
              <Pill $tone="neutral">fora da janela de etapas do lote</Pill>
            )}
          </LinhaEtapaLote>

          {erroEtapaLote && (
            <WarningMsg style={{ marginTop: 10 }}>{erroEtapaLote}</WarningMsg>
          )}

          {etapaCodigoLote
            && podeOperarEtapaLote
            && !ehCheckpointEtapaLote && (
              <LinhaAcoes style={{ marginTop: 10 }}>
                <AsyncButton
                  variant="secondary"
                  silentError
                  silentSuccess
                  onClick={() => executarEtapaLote("iniciar")}
                >
                  Iniciar em bloco
                </AsyncButton>
                <AsyncButton
                  silentError
                  silentSuccess
                  onClick={() => executarEtapaLote("concluir")}
                >
                  Concluir em bloco
                </AsyncButton>
              </LinhaAcoes>
            )}

          {etapaCodigoLote && (
            <DicaAcao style={{ marginTop: 8 }}>
              A ação vale para o LOTE: cascateia em bloco para as etapas dos
              ambientes membros. Ambiente congelado ou fora da etapa é pulado
              e listado aqui — a timeline e o buffer de cada ambiente seguem
              íntegros.
            </DicaAcao>
          )}

          {resultadoBloco && (
            <>
              <InfoMsg style={{ marginTop: 10 }}>
                {resultadoBloco.quantidade} ambiente(s){" "}
                {resultadoBloco.acao === "iniciar"
                  ? "iniciado(s)"
                  : "concluído(s)"}{" "}
                em bloco.
              </InfoMsg>
              {resultadoBloco.pulados.length > 0 && (
                <WarningMsg
                  style={{ marginTop: 8, alignItems: "flex-start" }}
                >
                  <div>
                    <strong>
                      {resultadoBloco.pulados.length} ambiente(s) pulado(s):
                    </strong>
                    <ListaPulados>
                      {resultadoBloco.pulados.map((p, idx) => (
                        <li key={`${p.ambiente || "amb"}-${idx}`}>
                          {p.ambiente} — {p.motivo}
                        </li>
                      ))}
                    </ListaPulados>
                  </div>
                </WarningMsg>
              )}
            </>
          )}
        </Card>
      )}

      {lote.situacao === "ABERTO" && podeOtimizar && (
        <Card>
          <SectionTitle>Ações — lote aberto</SectionTitle>
          <LinhaAcoes>
            {/* Abre slide-over (sem API) — botão comum, não AsyncButton. */}
            <SecondaryButton
              type="button"
              onClick={() => setPainelAcao("registrar-plano")}
            >
              Registrar plano externo
            </SecondaryButton>
            {/* Plano fictício só fora de produção: lá o servidor recusa. */}
            {PERMITE_INTEGRACAO_FICTICIA && (
              <AsyncButton
                silentError
                successMessage="Lote otimizado via provider mock."
                onClick={async () => {
                  setLote(await otimizarMock(request, lote.id));
                }}
              >
                Otimizar (plano de teste)
              </AsyncButton>
            )}
          </LinhaAcoes>
          <DicaAcao>
            Envie o lote ao PharuCut para otimizar o corte, ou registre aqui o
            plano feito em outro otimizador. A produção nunca trava sem o
            PharuCut: o registro externo também vale.
          </DicaAcao>
        </Card>
      )}

      {lote.situacao === "LIBERADO" && podeImprimir && (lote.pecas_count ?? 0) > 0 && (
        <Card>
          <SectionTitle>Etiquetas</SectionTitle>
          <LinhaAcoes>
            <AsyncButton
              variant="secondary"
              silentError
              silentSuccess
              onClick={imprimirEtiquetasDoLote}
            >
              Imprimir etiquetas do lote
            </AsyncButton>
          </LinhaAcoes>
          <DicaAcao>
            Gera 1 página 50×30mm (Code 128) por peça ativa do lote — a
            seccionadora cola na chapa. Reimpressão exige motivo (auditada).
          </DicaAcao>
        </Card>
      )}

      {lote.situacao === "OTIMIZADO" && (podeOtimizar || podeLiberar) && (
        <Card>
          <SectionTitle>Ações — lote otimizado</SectionTitle>
          <LinhaAcoes>
            {podeOtimizar && !lote.requisicao_numero && (
              <AsyncButton
                variant="secondary"
                silentError
                successMessage="Requisição de compra gerada."
                onClick={async () => {
                  setLote(await gerarRequisicao(request, lote.id));
                }}
              >
                Gerar requisição de compra
              </AsyncButton>
            )}
            {podeLiberar && (
              <PrimaryButton
                type="button"
                onClick={() => setPainelAcao("liberar")}
              >
                Liberar lote
              </PrimaryButton>
            )}
          </LinhaAcoes>
          {lote.requisicao_numero ? (
            <InfoMsg>
              Requisição <Mono>{lote.requisicao_numero}</Mono> gerada —
              acompanhe o atendimento em Suprimentos.
            </InfoMsg>
          ) : (
            <DicaAcao>
              A requisição agrega as chapas do BOM de todos os ambientes do
              lote numa única RC para Suprimentos (idempotente). Liberar o
              lote materializa as peças de produção.
            </DicaAcao>
          )}
        </Card>
      )}

      <Card>
        <SectionTitle>Ambientes do lote</SectionTitle>
        {(lote.ambientes || []).length === 0 ? (
          <EmptyMsg>Nenhum ambiente vinculado ao lote.</EmptyMsg>
        ) : (
          <GradeAmbientes>
            {lote.ambientes.map((a) => (
              <AmbienteCard
                key={a.id}
                densidade="compact"
                mostrarContexto
                codigo={a.numero}
                nome={a.nome}
                pedidoNumero={a.pedido_numero}
                cliente={a.cliente_nome}
                faseLabel={FASE_INFO[a.fase]?.curto || a.fase}
                pecasTotal={a.pecas_total}
                progressoPct={a.progresso_pct}
                bufferPct={a.buffer?.pct ?? 0}
                bufferZona={a.buffer?.zona || "verde"}
                congelado={a.congelado}
                onClick={() => navigate(`/producao/ambientes/${a.id}`)}
              />
            ))}
          </GradeAmbientes>
        )}
      </Card>

      {lote.situacao !== "ABERTO" && (
        <Card>
          <SectionTitle>Plano de corte</SectionTitle>
          {temPlanoEstruturado ? (
            <>
              <ResumoPlano>
                <span>
                  Provider: <strong>{plano.provider || "–"}</strong>
                </span>
                <span>
                  Algoritmo: <strong>{plano.algoritmo || "–"}</strong>
                </span>
                <span>
                  Chapas:{" "}
                  <strong>
                    <Mono>{plano.qtd_chapas ?? chapasPlano.length}</Mono>
                  </strong>
                </span>
                {lote.otimizado_em && (
                  <span>
                    Otimizado em:{" "}
                    <strong>
                      <Mono>{formatarDataHoraCurta(lote.otimizado_em)}</Mono>
                    </strong>
                  </span>
                )}
              </ResumoPlano>
              <TabelaChapas>
                <colgroup>
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "60%" }} />
                  <col style={{ width: "30%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Material</th>
                    <th>Aproveitamento</th>
                  </tr>
                </thead>
                <tbody>
                  {chapasPlano.map((chapa, idx) => (
                    <tr key={chapa.indice ?? idx}>
                      <td>
                        <Mono>{(chapa.indice ?? idx) + 1}</Mono>
                      </td>
                      <td title={chapa.material || ""}>
                        {chapa.material || "–"}
                      </td>
                      <td>
                        <Mono>
                          {chapa.aproveitamento_pct != null
                            ? `${chapa.aproveitamento_pct}%`
                            : "–"}
                        </Mono>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TabelaChapas>
            </>
          ) : (
            <EmptyMsg>
              Plano registrado externamente (sem detalhes de corte no ERP).
              {plano?.observacao ? ` Observação: “${plano.observacao}”` : ""}
            </EmptyMsg>
          )}
        </Card>
      )}

      <RegistrarPlanoForm
        aberto={painelAcao === "registrar-plano"}
        onFechar={() => setPainelAcao(null)}
        loteId={lote.id}
        onAtualizado={setLote}
      />
      <LiberarForm
        aberto={painelAcao === "liberar"}
        onFechar={() => setPainelAcao(null)}
        lote={lote}
        onAtualizado={setLote}
      />
    </PageContainer>
  );
}
